/**
 * API COVERAGE — VendorsController (/api/vendors) and VendorListsController (/api/vendor-lists): Module 2 Vendors and
 * DMCs, Part 1 (V1–V5, V8, V9, V11, V19, V20).
 */
import { api, http } from '@/lib/apiClient';
import type { BulkResult, NamedRef, Skipped } from './places.api';
import type { PoiImportCheck, PoiImportRow } from './pois.api';
import type { ImportApi } from './subtours.api';

export type VendorStatus = 'Draft' | 'PendingApproval' | 'Active' | 'Inactive' | 'Blocked';
export type KycStatus = 'NotSubmitted' | 'Pending' | 'Approved' | 'CorrectionAsked' | 'Rejected';
export type BasedIn = 'India' | 'Abroad';
export type RateBasis = 'Net' | 'Commission';

export const VENDOR_STATUS_LABEL: Record<VendorStatus, string> = {
  Draft: 'Draft', PendingApproval: 'Pending approval', Active: 'Active', Inactive: 'Inactive', Blocked: 'Blocked',
};
/** V1 shows "KYC approved" or "KYC pending"; the detail names the exact state. */
export const KYC_LABEL: Record<KycStatus, string> = {
  NotSubmitted: 'KYC pending', Pending: 'KYC pending', Approved: 'KYC approved', CorrectionAsked: 'KYC correction asked', Rejected: 'KYC rejected',
};
export const KYC_DETAIL: Record<KycStatus, string> = {
  NotSubmitted: 'Not sent for approval yet', Pending: 'Waiting for approval', Approved: 'Approved', CorrectionAsked: 'Correction asked', Rejected: 'Rejected',
};

export interface VendorAddress {
  addressLine?: string | null; city?: string | null; stateName?: string | null; stateCode?: string | null; countryIso2?: string | null;
  countryName?: string | null; postalCode?: string | null; latitude?: number | null; longitude?: number | null;
}
export interface VendorContact { id?: number | null; department: string; name: string; phone?: string | null; hasWhatsApp: boolean; email?: string | null; isPrimary: boolean }
export interface Slab { daysFrom: number; daysTo?: number | null; chargePct: number; isNoShow: boolean }

export interface BankIn {
  holderName: string; accountType: string; accountNumber?: string | null; confirmAccountNumber?: string | null; ifsc?: string | null;
  bankName?: string | null; bankBranch?: string | null; proofFileId?: number | null;
}
export interface BankOut { id: number; holderName: string; accountType: string; accountMasked: string; ifsc?: string | null; bankName?: string | null; bankBranch?: string | null; proofFileId?: number | null }
export interface BranchIn { id?: number | null; gstin?: string | null; address: VendorAddress; isDefault: boolean; bank?: BankIn | null }
export interface BranchOut { id: number; gstin?: string | null; gstStatus: 'Registered' | 'Unregistered'; address: VendorAddress; isDefault: boolean; bank?: BankOut | null }

export interface TermsIn { paymentTerm?: string | null; creditDays?: number | null; advancePct?: number | null; slabs: Slab[]; tdsNature?: string | null }
export interface TermsOut extends TermsIn {
  paymentTermCode?: string | null; tdsLabel?: string | null; tdsRate?: number | null; tdsRateNote: string; summary: string;
}

export interface VendorListItem {
  id: number; code: string; businessName: string; supplies: string[]; destinations: string; city?: string | null; stateName?: string | null;
  countryName?: string | null; gstText: string; kycStatus: KycStatus; status: VendorStatus; basedIn: BasedIn;
}
export interface VendorList { items: VendorListItem[]; total: number; all: number; kycWaiting: number }

export interface Vendor {
  id: number; code: string; basedIn: BasedIn; supplies: string[]; gstin?: string | null; businessName: string; legalName?: string | null;
  businessType?: string | null; pan?: string | null; foreignTaxId?: string | null; phone?: string | null; email?: string | null; address: VendorAddress;
  website?: string | null; featuredFileId?: number | null; rateBasis: RateBasis; commissionPct?: number | null;
  servedDestinations: NamedRef[]; preferredDestinations: NamedRef[]; contacts: VendorContact[]; branches: BranchOut[]; terms: TermsOut;
  status: VendorStatus; kycStatus: KycStatus; kycApprovedAt?: string | null; kycNote?: string | null; agreementVersion?: string | null;
  agreementSignedAt?: string | null; portalLoginActive: boolean; blockedReason?: string | null; blockedAt?: string | null; sharedPanConfirmed: boolean;
  subTours: NamedRef[]; canSeeFinance: boolean; missingForApproval: string[]; about?: string | null; aboutOriginal?: string | null;
}

export interface SaveVendor {
  sendForApproval: boolean; basedIn: BasedIn; supplies: string[]; gstin?: string | null; businessName: string; legalName?: string | null;
  businessType?: string | null; pan?: string | null; foreignTaxId?: string | null; phone?: string | null; email?: string | null; address: VendorAddress;
  website?: string | null; featuredFileId?: number | null; rateBasis: RateBasis; commissionPct?: number | null; servedDestinationIds: number[];
  preferredDestinationIds: number[]; contacts: VendorContact[]; branches: BranchIn[]; terms?: TermsIn | null; confirmSharedPan?: boolean;
  about?: string | null; keepAboutAsTyped?: boolean;
}

export interface TaxMatch { id: number; code: string; businessName: string; supplies: string[]; pan?: string | null; gstin?: string | null; foreignTaxId?: string | null; field: 'PAN' | 'GSTIN' | 'TaxId' }
export interface TaxCheck { matches: TaxMatch[]; sharedPanAllowed: boolean; message?: string | null }
export interface GstFetch {
  gstin: string; legalName?: string | null; tradeName?: string | null; businessType?: string | null; pan?: string | null; registrationStatus?: string | null;
  addressLine?: string | null; city?: string | null; state?: string | null; country?: string | null; postalCode?: string | null; matches: TaxMatch[];
}
export interface IfscDetails { ifsc: string; bank: string; branch?: string | null; address?: string | null; city?: string | null; state?: string | null }
export interface VendorFilters { countries: { iso2: string; name: string }[]; states: string[]; cities: string[] }

export interface VendorListParams {
  search?: string; supply?: string; country?: string; state?: string; city?: string; destinationId?: number; kyc?: string; status?: string;
  page?: number; pageSize?: number;
}

// ---- Lists (V20) and TDS (V19) ----

export type ListKind =
  | 'SupplyType' | 'BusinessType' | 'ContactDepartment' | 'PaymentTerm' | 'CreditPeriod' | 'CancellationTemplate' | 'RbiPurposeCode'
  | 'KycDocRule' | 'BankAccountType'
  // Module 3: the lists behind the hotel dropdowns (same table and screens). Amenities have their own master (amenitiesApi).
  | 'HotelCategory' | 'HotelTypeTag' | 'BedType' | 'RoomView' | 'MealPlan';
export const HOTEL_LIST_KINDS: ListKind[] = ['HotelCategory', 'HotelTypeTag', 'BedType', 'RoomView', 'MealPlan'];
/** Groups of the hotel's amenities checklist (the amenities master, /amenities). */
export const AMENITY_GROUPS = ['General', 'Leisure', 'In the room'];
export interface KycDocRule { basedIn: BasedIn; country?: string | null; businessType?: string | null; document: string; needed: string }
export interface ListItem {
  id: number; kind: ListKind; name: string; code?: string | null; number?: number | null; description?: string | null; slabs?: Slab[] | null;
  rule?: KycDocRule | null; sortOrder: number; isActive: boolean; isSystem: boolean; usedBy: number;
}
export interface SaveListItem {
  name: string; code?: string | null; number?: number | null; description?: string | null; slabs?: Slab[] | null; rule?: KycDocRule | null;
  sortOrder?: number | null; isActive: boolean;
}
export interface ListTile { kind: ListKind | 'Tds'; title: string; preview: string; usedIn: string; count: number }
export type TdsStatus = 'Active' | 'CaToVerify' | 'Inactive';
export type Deductee = 'IndividualHuf' | 'Others' | 'Any';
export interface TdsRow {
  id: number; act: '2025' | '1961'; section?: string | null; oldSection?: string | null; nature: string; paymentCode?: string | null; deducteeType: Deductee;
  rate?: number | null; noPanRate?: number | null; singleThreshold?: number | null; annualThreshold?: number | null; validFrom: string; validTo?: string | null;
  status: TdsStatus; usedBy: number;
}
export type SaveTdsRow = Omit<TdsRow, 'id' | 'usedBy'>;
export interface TdsNature { nature: string; label: string; section?: string | null; oldSection?: string | null; rateIndividualHuf?: number | null; rateOthers?: number | null; noPanRate?: number | null; verified: boolean }

export const TDS_STATUS_LABEL: Record<TdsStatus, string> = { Active: 'Active', CaToVerify: 'CA to verify', Inactive: 'Inactive' };
export const DEDUCTEE_LABEL: Record<Deductee, string> = { IndividualHuf: 'Individual / HUF', Others: 'Others (company, firm, LLP)', Any: 'Any' };

async function download(url: string, body: unknown | undefined, fallback: string) {
  const res = body === undefined ? await http.get(url, { responseType: 'blob' }) : await http.post(url, body, { responseType: 'blob' });
  const name = /filename="?([^";]+)"?/i.exec(res.headers['content-disposition'] ?? '')?.[1] ?? fallback;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href; a.download = name; a.click();
  URL.revokeObjectURL(href);
}

const base = '/vendors';
export const vendorsApi = {
  list: (p: VendorListParams) => api.get<VendorList>(base, p as Record<string, unknown>),
  filters: () => api.get<VendorFilters>(`${base}/filters`),
  options: (p: { supply?: string; search?: string; usableOnly?: boolean } = {}) => api.get<NamedRef[]>(`${base}/options`, p),
  get: (id: number) => api.get<Vendor>(`${base}/${id}`),
  create: (b: SaveVendor) => api.post<Vendor>(base, b),
  update: (id: number, b: SaveVendor) => api.put<Vendor>(`${base}/${id}`, b),
  checkTax: (b: { pan?: string | null; gstin?: string | null; foreignTaxId?: string | null; exceptId?: number | null; supplies?: string[] }) =>
    api.post<TaxCheck>(`${base}/check-tax`, b),
  fetchGst: (gstin: string, exceptId?: number | null) => api.get<GstFetch>(`${base}/gst/${encodeURIComponent(gstin)}`, { exceptId: exceptId ?? undefined }),
  ifsc: (ifsc: string) => api.get<IfscDetails>(`${base}/ifsc/${encodeURIComponent(ifsc)}`),
  ifscSearch: (q: string) => api.get<IfscDetails[]>(`${base}/ifsc-search`, { q }),
  setStatus: (ids: number[], status: 'Active' | 'Inactive') => api.post<BulkResult>(`${base}/status`, { ids, status }),
  block: (ids: number[], reason: string) => api.post<BulkResult>(`${base}/block`, { ids, reason }),
  unblock: (ids: number[]) => api.post<BulkResult>(`${base}/unblock`, { ids }),
  deletePreview: (ids: number[]) => api.post<{ willDelete: NamedRef[]; willSkip: Skipped[] }>(`${base}/delete-preview`, { ids }),
  remove: (ids: number[]) => api.post<BulkResult>(`${base}/delete`, { ids }),
  exportExcel: (body: { ids?: number[] } & VendorListParams) => download(`${base}/export`, body, 'vendors.xlsx'),
};

export const vendorImportApi: ImportApi = {
  sample: () => download(`${base}/sample`, undefined, 'vendors_sample.xlsx'),
  checkImport: (file: File) => { const f = new FormData(); f.append('file', file); return api.post<PoiImportCheck>(`${base}/import/check`, f); },
  importRows: (fileName, rows, skipErrorRows) =>
    api.post<{ batchId: number; imported: number; skipped: number; message: string }>(`${base}/import`, { fileName, rows, skipErrorRows }),
  undoImport: (batchId) => api.post<string>(`${base}/import/${batchId}/undo`),
};

const lists = '/vendor-lists';
export const vendorListsApi = {
  hub: () => api.get<ListTile[]>(lists),
  hotelHub: () => api.get<ListTile[]>(`${lists}/hotel-hub`),
  items: (kind: ListKind) => api.get<ListItem[]>(`${lists}/${kind}`),
  add: (kind: ListKind, b: SaveListItem) => api.post<ListItem>(`${lists}/${kind}`, { ...b, kind }),
  /** "+ Add new" from a vendor form dropdown. */
  quickAdd: (kind: ListKind, b: SaveListItem) => api.post<ListItem>(`${lists}/${kind}/quick-add`, { ...b, kind }),
  update: (kind: ListKind, id: number, b: SaveListItem) => api.put<ListItem>(`${lists}/${kind}/${id}`, { ...b, kind }),
  remove: (kind: ListKind, id: number) => api.del<string>(`${lists}/${kind}/${id}`),
  tds: () => api.get<TdsRow[]>(`${lists}/tds`),
  tdsNatures: () => api.get<TdsNature[]>(`${lists}/tds/natures`),
  addTds: (b: SaveTdsRow) => api.post<TdsRow>(`${lists}/tds`, b),
  updateTds: (id: number, b: SaveTdsRow) => api.put<TdsRow>(`${lists}/tds/${id}`, b),
  removeTds: (id: number) => api.del<string>(`${lists}/tds/${id}`),
};

/** Import for a list (or the TDS tax table): check, then import; no undo (lists are edited row by row). */
export const listImportApi = (kind: ListKind | 'Tds'): ImportApi => ({
  sample: () => download(`${lists}/${kind}/sample`, undefined, `${kind}_sample.xlsx`),
  checkImport: (file: File) => { const f = new FormData(); f.append('file', file); return api.post<PoiImportCheck>(`${lists}/${kind}/import/check`, f); },
  importRows: async (_fileName: string, rows: PoiImportRow[], skipErrorRows: boolean) => {
    const message = await api.post<string>(`${lists}/${kind}/import`, { rows, skipErrorRows });
    return { batchId: 0, imported: 0, skipped: 0, message };
  },
  undoImport: () => Promise.resolve(''),
});

/** Dropdown values of one list (active ones), for the vendor form. */
export const LIST_KEY = 'vendor-list';

// ---- KYC (V6, V7) ----

export interface VendorSignatory {
  ownerNames?: string | null; name?: string | null; designation?: string | null; mobile?: string | null; mobileVerified: boolean;
  mobileVerifiedAt?: string | null; email?: string | null; emailVerified: boolean; emailVerifiedAt?: string | null;
}
export type OwnerIdType = 'Aadhaar' | 'Passport' | 'NationalId';
export interface VendorOwnerId {
  idType: OwnerIdType; name?: string | null; idNumber?: string | null; dateOfBirth?: string | null; nationality?: string | null;
  validUntil?: string | null; address?: string | null; fileName?: string | null; scanned: boolean; fromOcr: string[];
}
export interface VendorKycDoc { id: number; docType: string; fileName: string; contentType: string; checkResult?: string | null; checkMismatch: boolean; status: 'Waiting' | 'Verified' | 'Mismatch'; uploadedAt: string }
export interface VendorKycRow { document: string; needed: string; required: boolean; kind: 'Rule' | 'OwnerId' | 'Extra'; file?: VendorKycDoc | null }
export interface ForeignBank {
  beneficiaryName?: string | null; bankName?: string | null; bankAddress?: string | null; accountMasked?: string | null; accountType?: string | null;
  swift?: string | null; localBankCode?: string | null; intermediarySwift?: string | null; currency: string; purposeCode?: string | null;
  bankIn?: 'India' | 'Abroad'; ifsc?: string | null; bankBranch?: string | null;
}
export interface VendorKyc {
  vendorId: number; basedIn: BasedIn; kycStatus: KycStatus; kycNote?: string | null; signatory: VendorSignatory; ownerId?: VendorOwnerId | null;
  documents: VendorKycRow[]; foreignBank?: ForeignBank | null; missing: { field: string; message: string }[]; message?: string | null;
}
export interface ForeignBankIn {
  beneficiaryName?: string | null; bankName?: string | null; bankAddress?: string | null; accountNumber?: string | null; confirmAccountNumber?: string | null;
  accountType?: string | null; swift?: string | null; localBankCode?: string | null; intermediarySwift?: string | null; currency?: string | null; purposeCode?: string | null;
  bankIn?: 'India' | 'Abroad' | null; ifsc?: string | null; bankBranch?: string | null;
}
export interface SaveKyc {
  ownerNames?: string | null; signatoryName?: string | null; designation?: string | null; mobile?: string | null; email?: string | null;
  ownerIdType?: OwnerIdType | null; ownerName?: string | null; ownerIdNumber?: string | null; ownerDateOfBirth?: string | null; ownerNationality?: string | null;
  ownerValidUntil?: string | null; ownerAddress?: string | null; foreignBank?: ForeignBankIn | null;
}
export interface VendorOtpSent { channel: 'mobile' | 'email'; maskedDestination: string; expiresInSeconds: number; resendAfterSeconds: number; codeLength: number; via: string }

const kyc = (id: number) => `${base}/${id}/kyc`;
export const vendorKycApi = {
  get: (id: number) => api.get<VendorKyc>(kyc(id)),
  save: (id: number, b: SaveKyc) => api.put<VendorKyc>(kyc(id), { ...b, vendorId: id }),
  sendOtp: (id: number, channel: 'mobile' | 'email') => api.post<VendorOtpSent>(`${kyc(id)}/otp/send`, { channel }),
  verifyOtp: (id: number, channel: 'mobile' | 'email', code: string) => api.post<VendorKyc>(`${kyc(id)}/otp/verify`, { channel, code }),
  uploadOwnerId: (id: number, file: File, idType: OwnerIdType) => { const f = new FormData(); f.append('file', file); f.append('idType', idType); return api.post<VendorKyc>(`${kyc(id)}/owner-id`, f); },
  uploadDocument: (id: number, file: File, docType: string) => { const f = new FormData(); f.append('file', file); f.append('docType', docType); return api.post<VendorKyc>(`${kyc(id)}/documents`, f); },
  removeDocument: (id: number, docId: number) => api.del<VendorKyc>(`${kyc(id)}/documents/${docId}`),
  /** Opens a KYC file in a new tab (authenticated fetch). docId null = the owner ID card. */
  open: async (id: number, docId: number | null) => {
    const res = await http.get(docId == null ? `${kyc(id)}/owner-id/file` : `${kyc(id)}/documents/${docId}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

// ---- Approvals centre (V13, V14, V21) ----

export interface ApprovalItem { kind: 'Kyc' | 'RateChange'; id: number; title: string; subtitle: string; submittedAt?: string | null; ticked: number; total: number }
export interface KycSummaryRow { item: string; entered: string; read?: string | null; check: 'Match' | 'Differs' | 'Verified' | 'Not read' }
export interface ReviewEdit { key: string; label: string; old?: string | null; new?: string | null }
export interface KycSummary {
  vendorId: number; code: string; name: string; subtitle: string; kycStatus: KycStatus; rows: KycSummaryRow[]; warnings: string[];
  documents: { id?: number | null; document: string; fileName: string }[]; ticked: number; total: number; edits: ReviewEdit[];
}
export interface ReviewItem {
  key: string; label: string; value?: string | null; input: 'text' | 'textarea' | 'select' | 'date' | 'readonly' | 'document'; options?: string[] | null;
  required: boolean; ticked: boolean; editedFrom?: string | null; badge?: string | null; documentId?: number | null; hint?: string | null;
}
export interface ReviewSection { key: string; title: string; tab: string; items: ReviewItem[] }
export interface VendorReview {
  vendorId: number; code: string; name: string; submittedAt?: string | null; source: string; kycStatus: KycStatus; sections: ReviewSection[];
  ticked: number; total: number; edits: ReviewEdit[]; aboutOriginal?: string | null;
}
export interface RateChange {
  id: number; vendorId: number; vendorName: string; vendorCode: string; title: string; sentAt: string; effectiveFrom?: string | null;
  lines: { item: string; now: string; proposed: string; change: string }[]; status: string; sourceType: string;
}

export const approvalsApi = {
  list: () => api.get<{ items: ApprovalItem[] }>('/approvals'),
  count: () => api.get<number>('/approvals/count'),
  summary: (vendorId: number) => api.get<KycSummary>(`/approvals/vendors/${vendorId}/summary`),
  review: (vendorId: number) => api.get<VendorReview>(`/approvals/vendors/${vendorId}/review`),
  saveReview: (vendorId: number, ticked: string[], edits: { key: string; value: string | null }[]) =>
    api.put<VendorReview>(`/approvals/vendors/${vendorId}/review`, { ticked, edits }),
  decide: (vendorId: number, decision: 'Approve' | 'Correction' | 'Reject', reason?: string | null) =>
    api.post<string>(`/approvals/vendors/${vendorId}/decision`, { decision, reason }),
  approveMany: (ids: number[]) => api.post<BulkResult>('/approvals/vendors/approve', { ids }),
  rateChange: (id: number) => api.get<RateChange>(`/approvals/rate-changes/${id}`),
  decideRate: (id: number, approve: boolean, note?: string | null) => api.post<string>(`/approvals/rate-changes/${id}/decision`, { approve, note }),
};
export const APPROVALS_KEY = 'approvals';

// ---- Invite, onboarding link (V10), portal login and agreements (M2.7–M2.9) ----

export interface VendorInvite { link: string; emailSent: boolean; whatsAppUrl?: string | null; message: string }
export interface AgreementInfo { templateId: number; title: string; version: string; appliesTo: string; body: string; needsSignature: boolean }
export interface AgreementSignature { id: number; title: string; version: string; signedAt: string; signatoryName: string; designation?: string | null; ip?: string | null; otpProof: string }
export interface VendorAgreements {
  loginId?: string | null; loginActive: boolean; lastLoginAt?: string | null; current?: AgreementInfo | null; signatures: AgreementSignature[]; portalUrl: string;
}

export const vendorPortalAdminApi = {
  onboardingLink: () => api.get<{ url: string }>(`${base}/onboarding-link`),
  invite: (id: number, b: { email?: string | null; whatsApp?: string | null; byEmail: boolean; byWhatsApp: boolean }) => api.post<VendorInvite>(`${base}/${id}/invite`, b),
  agreements: (id: number) => api.get<VendorAgreements>(`${base}/${id}/agreements`),
  agreementPdf: (id: number, signatureId?: number) =>
    download(`${base}/${id}/agreements/pdf${signatureId ? `?signatureId=${signatureId}` : ''}`, undefined, 'agreement.pdf'),
  resendLogin: (id: number) => api.post<string>(`${base}/${id}/portal-login/resend`),
};

// ---- Settings › Document templates › Vendor agreements ----

export interface AgreementTemplate {
  id: number; supplyType?: string | null; appliesTo: string; title: string; version?: string | null; body: string; isPublished: boolean; publishedAt?: string | null;
  signed: number; isCurrent: boolean;
}
export const agreementTemplatesApi = {
  list: () => api.get<{ items: AgreementTemplate[]; supplyTypes: string[] }>('/agreement-templates'),
  create: (b: { supplyType: string | null; title: string; body: string }) => api.post<AgreementTemplate>('/agreement-templates', b),
  update: (id: number, b: { supplyType: string | null; title: string; body: string }) => api.put<AgreementTemplate>(`/agreement-templates/${id}`, b),
  publish: (id: number) => api.post<AgreementTemplate>(`/agreement-templates/${id}/publish`),
  remove: (id: number) => api.del<string>(`/agreement-templates/${id}`),
};
